import React from "react";
import { clsx } from "clsx";

import {
    SECONDARY_ITEM_MEDIA_PRIORITY,
    SECONDARY_ITEM_MEDIA_CONFIG,
    CURATION_WARNING,
} from "~components/modules/lineup-content/variants/weekend-landscape/LineupContentWeekendLandscape";
import { PreviewErrorCard } from "~components/shared/card/Card";
import Headline from "~components/shared/headline/Headline";
import {
    AuthorImageMedia,
    Media,
    MediaFormat,
    type MediaConfig,
    sourceHasValidMedia,
} from "~components/shared/media";
import OptionalEyebrow from "~components/shared/optional-eyebrow/OptionalEyebrow";
import PhoenixByline from "~components/shared/phoenix-byline/PhoenixByline";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import TertiaryItems from "~components/shared/tertiary-items/TertiaryItems";
import { VariantBase } from "~components/shared/variant-base/VariantBase";
import WeekendHeader from "~components/shared/weekend-header/WeekendHeader";
import { HEADLINE_SIZE } from "~types/lineup";
import {
    type VariationComponent,
    ZoneColumnCombo,
    isTextOverMediaItemVariation,
    type CuratedStory,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentWeekendPortrait.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentWeekendPortrait.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];

// Media consts specific to the Portrait version
// of the Weekend Module
export const LEDE_ITEM_MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.verticalVideo,
    MediaFormat.loopingVideo,
    MediaFormat.defaultImage,
];
export const LEDE_ITEM_OVERLAY_MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.loopingVideo,
    MediaFormat.defaultImage,
];
const LEDE_ITEM_MEDIA_CONFIG: MediaConfig = {
    desktopMaxWidth: 494,
    mobileMaxWidth: 740,
    aspectRatio: 2 / 3,
    showCaption: true,
};

export default function LineupContentWeekendPortrait(
    props: VariationComponent
) {
    const { items = [], labelUrl } = props;

    const integrity = integrityCheck({ ...props });
    if (integrity.length) return integrityError(integrity);

    if (!items?.length) return null;
    const [ledeItem, ...supportingItems] = items;

    const { franchise, updatedAt, publishedAt } =
        "franchise" in ledeItem ? (ledeItem as CuratedStory) : {};

    const useTextOverMedia = isTextOverMediaItemVariation(ledeItem.variation);

    // We need to limit the available Media Formats if editors
    // have applied either of the text-over-image treatments because
    // users are unable to interact with Media when we overlay text.
    // Embedded i-frames, Videos-on-demand, Livestream players and Snapshot Charts
    // cannot be used, for example.
    const ledeItemMediaPriority = useTextOverMedia
        ? LEDE_ITEM_OVERLAY_MEDIA_PRIORITY
        : LEDE_ITEM_MEDIA_PRIORITY;

    return (
        <VariantBase
            source={props}
            className={styles.LineupContentWeekendPortrait}
            gridClassName={styles.grid}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <WeekendHeader titleUrl={labelUrl || "/weekend"} />
            <div
                className={clsx(
                    styles.ledeAndSecondaryItems,
                    useTextOverMedia && styles.textOverImage
                )}
            >
                <div className={styles.ledeItem}>
                    <div className={styles.mobileLedeItemMedia}>
                        <Media
                            source={ledeItem}
                            priority={ledeItemMediaPriority}
                            config={{
                                ...LEDE_ITEM_MEDIA_CONFIG,
                                aspectRatio: 3 / 2,
                            }}
                        />
                    </div>
                    <a
                        href={ledeItem.url}
                        className={styles.storyLink}
                        data-component="story-link"
                        data-link-index={1}
                    >
                        <div
                            className={styles.storyBlock}
                            data-component="story-block"
                        >
                            <div className={styles.optionalEyebrowContainer}>
                                <OptionalEyebrow
                                    label={ledeItem.label}
                                    franchise={franchise}
                                    updatedAt={updatedAt}
                                    publishedAt={publishedAt}
                                    textLabel={ledeItem.textLabel}
                                    presetLabel={ledeItem.labelDropdown}
                                    nicodes={ledeItem.nicodes}
                                    className={styles.optionalEyebrow}
                                />
                            </div>
                            <Headline
                                text={ledeItem.headline}
                                isPhoenix
                                className={styles.headline}
                                size={HEADLINE_SIZE.UNSTYLED}
                            />
                            <div className={styles.authorImageAndByline}>
                                <AuthorImageMedia
                                    authorImage={ledeItem?.media?.authorImage}
                                    config={
                                        {
                                            desktopMaxWidth: 62,
                                            mobileMaxWidth: 62,
                                            aspectRatio: 1,
                                            className: styles.authorImage,
                                        } as MediaConfig
                                    }
                                />
                                <PhoenixByline
                                    forceShowByline
                                    story={ledeItem}
                                />
                            </div>
                        </div>
                    </a>
                    <div className={styles.ledeItemMedia}>
                        <Media
                            source={ledeItem}
                            priority={ledeItemMediaPriority}
                            config={LEDE_ITEM_MEDIA_CONFIG}
                        />
                    </div>
                </div>
                <div className={styles.secondaryItems}>
                    {supportingItems.length > 0 &&
                        supportingItems.slice(0, 3).map((item, index) => {
                            const isImageless =
                                Boolean(item?.media?.optOut) ||
                                !sourceHasValidMedia(
                                    item,
                                    SECONDARY_ITEM_MEDIA_PRIORITY
                                );

                            return (
                                <div
                                    className={clsx(
                                        styles.item,
                                        isImageless && styles.imagelessItem
                                    )}
                                    key={item.url}
                                >
                                    <Media
                                        source={item}
                                        priority={SECONDARY_ITEM_MEDIA_PRIORITY}
                                        config={SECONDARY_ITEM_MEDIA_CONFIG}
                                    />
                                    <StoryBlock
                                        story={item}
                                        withOptionalEyebrow
                                        isExtraSmallHeadline
                                        isSecondaryStory
                                        storyIndex={index + 2}
                                        withDek={isImageless}
                                        disableFeaturedStyling
                                    />
                                </div>
                            );
                        })}
                    {supportingItems.length < 3 && (
                        <PreviewErrorCard message={CURATION_WARNING} />
                    )}
                </div>
            </div>
            {supportingItems.length > 3 && (
                <TertiaryItems items={supportingItems.slice(3, 6)} />
            )}
        </VariantBase>
    );
}
